<script setup lang="ts">
import { useMutation } from "@tanstack/vue-query";
import { useMessage } from "naive-ui";
import { computed, ref, watch } from "vue";
import { OpenItem } from "../../models/contador";
import {
    postOpenItemComment,
    postOpenItemCommentWithAttachments,
} from "../../services/contador/open-items.service";
import {
    deleteCommentFromIndexedDB,
    getCommentFromIndexedDB,
    initDB,
    saveCommentToIndexedDB,
} from "../../utils/indexedDbHelper";
import RichTextEditor from "../RichTextEditor.vue";
import { usePortalBreakpoints } from "../../hooks/usePortalBreakpoints.ts";
import { getUserEmail } from "../../config/firebase.ts";
const { isDesktop } = usePortalBreakpoints();

const props = defineProps<{
    openItem: OpenItem | null;
}>();

const emit = defineEmits<{
    commentAdded: [];
}>();

const message = useMessage();

function useDraftComment() {
    const commentContent = ref("");
    const fileList = ref<File[]>([]);
    const isLoading = ref(false);

    function getDraftKey(id: string | number | null) {
        return id ? `open-item-draft-${id}` : `open-item-draft-null`;
    }

    initDB();

    async function loadDraft(id: string | number | null) {
        isLoading.value = true;
        if (!id) {
            commentContent.value = "";
            fileList.value = [];
            isLoading.value = false;
            return;
        }
        const draft = await getCommentFromIndexedDB(getDraftKey(id));
        commentContent.value = draft?.text || "";
        fileList.value = draft?.files || [];
        isLoading.value = false;
    }

    async function saveDraft(id: string | number | null) {
        if (!id) return;
        await saveCommentToIndexedDB(getDraftKey(id), commentContent.value, fileList.value);
    }

    async function deleteDraft(id: string | number | null) {
        if (!id) return;
        await deleteCommentFromIndexedDB(getDraftKey(id));
    }

    return {
        commentContent,
        fileList,
        isLoading,
        loadDraft,
        saveDraft,
        deleteDraft,
    };
}

const { commentContent, fileList, loadDraft, saveDraft, deleteDraft } = useDraftComment();

watch(
    () => props.openItem?.id,
    async (id) => {
        await loadDraft(id ?? null);
    },
    { immediate: true },
);

// Save to composable/DB on change
watch([commentContent, fileList], async () => {
    if (!props.openItem?.id) return;
    await saveDraft(props.openItem.id);
});

const author = computed(() => getUserEmail() ?? "");

const { mutate: postCommentMutation, isPending: isPostingComment } = useMutation({
    mutationFn: async (payload: {
        open_item_id: string;
        content: string;
        author: string;
        attachments?: File[];
    }) => {
        if (payload.attachments && payload.attachments.length > 0) {
            return await postOpenItemCommentWithAttachments({
                open_item_id: payload.open_item_id,
                content: payload.content,
                author: payload.author,
                attachments: payload.attachments,
            });
        } else {
            return await postOpenItemComment({
                open_item_id: payload.open_item_id,
                content: payload.content,
                author: payload.author,
            });
        }
    },
    onSuccess() {
        message.success("Comment added successfully");
        // Reset the rich text editor state
        commentContent.value = "";
        fileList.value = [];
        if (props.openItem?.id) {
            deleteDraft(props.openItem.id);
        }
        // Emit event to parent to refetch open item
        emit("commentAdded");
    },
    onError(error: any) {
        message.error(`Failed to add comment: ${error?.message || error}`);
    },
});

async function handleSubmitComment() {
    if (!props.openItem) {
        message.error("No open item selected");
        return;
    }

    if (!commentContent.value.trim()) {
        message.error("Please enter a comment");
        return;
    }

    const payload = {
        open_item_id: props.openItem.id,
        content: commentContent.value,
        author: author.value,
        attachments: fileList.value.length > 0 ? fileList.value : undefined,
    };

    postCommentMutation(payload);
}

function handleFileListUpdate(files?: File[]) {
    fileList.value = files || [];
    // Saving handled by watcher
}
</script>

<template>
    <div
        class="p-4 pb-0"
        :class="{
            'px-0': !isDesktop,
        }"
    >
        <rich-text-editor
            v-model="commentContent"
            :file-list="fileList"
            :submit-disabled="isPostingComment"
            :is-submitting="isPostingComment"
            :show-submit-button="true"
            button-text="Send Comment"
            placeholder="Enter message..."
            :min-rows="3"
            :enable-mentions="false"
            @submit="handleSubmitComment"
            @update:file-list="handleFileListUpdate"
        >
            <slot />
        </rich-text-editor>
    </div>
</template>
